const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const MESSENGER_V2_OUTBOX_RETRY_DELAYS_MS = [1500, 4000, 8000, 15000, 30000, 60000];
export const MESSENGER_V2_OUTBOX_MAX_AUTOMATIC_ATTEMPTS =
  MESSENGER_V2_OUTBOX_RETRY_DELAYS_MS.length;

const PERMANENT_ERROR_CODES = new Set([
  'E2EE_READ_ONLY',
  'CONVERSATION_DELETED',
  'CONVERSATION_NOT_FOUND',
  'MEMBERSHIP_REVOKED',
  'NOT_AUTHORIZED',
]);

const RECOVERY_REQUIRED_ERROR_CODES = new Set(['E2EE_RECOVERY_BLOCKED']);

const resolveStatus = (error = null) => {
  const rawStatus = error?.status ?? error?.response?.status ?? error?.cause?.status ?? null;
  if (rawStatus == null || rawStatus === '') return null;
  const status = Number(rawStatus);
  return Number.isFinite(status) ? status : null;
};

export const classifyMessengerV2OutboundError = (error = null, { online = true } = {}) => {
  const code = toStringOrNull(error?.code)?.toUpperCase() || null;
  const status = resolveStatus(error);

  if (
    PERMANENT_ERROR_CODES.has(code) ||
    status === 401 ||
    status === 403 ||
    status === 404 ||
    status === 410 ||
    status === 400 ||
    status === 422
  ) {
    return {
      automatic: false,
      retryable: false,
      waitForOnline: false,
      reason: 'permanent',
      code,
      status,
    };
  }

  if (RECOVERY_REQUIRED_ERROR_CODES.has(code)) {
    return {
      automatic: false,
      retryable: true,
      waitForOnline: false,
      reason: 'recovery_required',
      code,
      status,
    };
  }

  if (online === false) {
    return {
      automatic: true,
      retryable: true,
      waitForOnline: true,
      reason: 'offline',
      code,
      status,
    };
  }

  const isTransientStatus =
    status === 408 || status === 409 || status === 425 || status === 429 || status >= 500;
  if (code === 'E2EE_NOT_READY' || isTransientStatus || status == null) {
    return {
      automatic: true,
      retryable: true,
      waitForOnline: false,
      reason: code === 'E2EE_NOT_READY' ? 'not_ready' : 'transient',
      code,
      status,
    };
  }

  return {
    automatic: false,
    retryable: true,
    waitForOnline: false,
    reason: 'manual',
    code,
    status,
  };
};

export const resolveMessengerV2OutboxFailure = ({
  error = null,
  attempts = 0,
  online = true,
} = {}) => {
  const classification = classifyMessengerV2OutboundError(error, { online });
  const normalizedAttempts = Math.max(0, Number(attempts) || 0);
  const canAutomaticallyRetry =
    classification.automatic &&
    (classification.waitForOnline ||
      normalizedAttempts < MESSENGER_V2_OUTBOX_MAX_AUTOMATIC_ATTEMPTS);
  const retryDelay = classification.waitForOnline
    ? null
    : MESSENGER_V2_OUTBOX_RETRY_DELAYS_MS[
        Math.min(normalizedAttempts, MESSENGER_V2_OUTBOX_RETRY_DELAYS_MS.length - 1)
      ];

  return {
    ...classification,
    automatic: canAutomaticallyRetry,
    retryAt: canAutomaticallyRetry && retryDelay != null ? Date.now() + retryDelay : null,
  };
};
